<template>
    <template v-if="!city">
      <spinner-view small />
    </template>

    <div v-else-if="listing.isAddressVerified" class="p-3">
      <div class="fw-semibold mb-3">
        Verified Address
      </div>
      <div class="mb-3">
        <SuggestedListingProperty
          :property="listing.verifiedProperty"
          :listing="listing"
        />
      </div>
    </div>

    <template v-else>
      <div class="tab-bar flex-shrink-0 border-bottom d-flex flex-row align-items-center gap-0">
        <div class="fw-medium px-3 flex-grow-1">Address Verification</div>
        <IconButton
          v-for="t in tabs"
          :key="t.id"
          @click="tab = t.id"
          :icon="t.icon"
          :title="t.label"
          :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': t.id === tab }]"
        />
      </div>

      <div class="flex-grow-1 overflow-auto bg-white" ref="scroller">

        <!-- Nearby Addresses -->
        <!-- @deprecated this is basically useless -->
        <!-- TODO this could be replaced by a geo query looking for properties within X meters of listing's latlng -->
        <VerificationTool
          v-if="tab === 'recommended'"
          :listing="listing"
          title="Nearby Properties"
          @show-location="args => $emit('showLocation', args)"
          @open-street-view="args => $emit('openStreetView', args)"
          :properties="loadingNearbyProperties ? [] : nearbyProperties">
          <template #description>
            <span v-if="isLoaded">
              Showing
              <template v-if="loadingNearbyProperties"><spinner-view small/> properties</template>
              <template v-else>{{formatNumber(nearbyProperties.length, 'property', 'properties')}}</template>
            </span>
            <span v-else>
              Finding properties
            </span>
            within {{ nearbyRadius }} ft of the listing's coordinates.
          </template>
          <div class="d-flex flex-row align-items-center gap-2">
            <select
              v-model.number="nearbyRadius"
              @change="refreshNearbyProperties"
              class="form-select form-select-sm"
              :disabled="loadingNearbyProperties"
              style="width: auto;">
              <option :value="100">100 ft</option>
              <option :value="200">200 ft</option>
              <option :value="400">400 ft</option>
              <option :value="800">800 ft</option>
              <option :value="1600">1600 ft</option>
            </select>
            <select
              v-model.number="nearbyLimit"
              @change="refreshNearbyProperties"
              class="form-select form-select-sm"
              :disabled="loadingNearbyProperties"
              style="width: auto;">
              <option :value="10">10 max</option>
              <option :value="25">25 max</option>
              <option :value="50">50 max</option>
              <option :value="100">100 max</option>
              <option :value="200">200 max</option>
            </select>
            <button v-if="!isLoaded" @click="refreshNearbyProperties" type="button" class="btn btn-primary btn-sm">Load</button>
          </div>
        </VerificationTool>

        <!-- License Selection -->
        <VerificationTool
          v-else-if="tab === 'licenses'"
          :listing="listing"
          title="Existing Licenses"
          description="Select a license on the map, or search by license number."
          @show-location="args => $emit('showLocation', args)"
          @open-street-view="args => $emit('openStreetView', args)"
          :properties="licenseProperties">
          <InputWithButton
            placeholder="Search"
            button-title="Search"
            v-model="licenseQuery"
            @go="searchLicenses"
            :loading="isSearchingLicenses"
          />
          <template v-if="!!licenses && !isSearchingLicenses">
            <div v-if="licenseProperties.length === 0" class="mt-1 small">
              We couldn't find any matches
            </div>
            <div v-else class="mt-1 small">
              Found {{ formatCount(licenseProperties.length, 'match', 'matches') }}
            </div>
          </template>
        </VerificationTool>

        <!-- Assessor Database -->
        <!-- TODO easy...the output of the AssessorSearch is properties and will flow nicely into the new VerificationTool -->
        <VerificationTool
          v-else-if="tab === 'assessor'"
          :listing="listing"
          title="Assessor Database"
          @show-location="args => $emit('showLocation', args)"
          @open-street-view="args => $emit('openStreetView', args)"
          :properties="assessorProperties"
        >
          <template #description>
            Search the <a :href="safeHref(listing.assessorUrl)" target="_blank" class="text-muted" rel="noopener noreferrer">assessor database</a> for
            information about properties.
          </template>
          <AssessorSearchView :city="city" @select="onAssessorProperties"/>
        </VerificationTool>
      </div>
    </template>
</template>

<script>
import { safeHref } from '@/safeRendering';
import {Listing} from "@/models/Listing";
import AssessorSearchView from "@/components/AssessorSearchView.vue";
import VerificationTool from "@/components/listing/VerificationTool.vue";
import {License} from "@/models/License";
import SpinnerView from "@/components/common/SpinnerView.vue";
import SuggestedListingProperty from "@/components/SuggestedListingProperty.vue";
import IconButton from "@/components/common/IconButton.vue";
import InputWithButton from "@/components/common/InputWithButton.vue";
import {PropertyDetails} from "@/models/PropertyDetails";
import {LocalStorage} from "@/models/LocalStorage";

const AUTO_LOAD_RECOMMENDED_PROPERTIES_KEY = 'autoLoadRecommendedProperties';

export default {
  components: {
    InputWithButton, IconButton, SuggestedListingProperty, SpinnerView, AssessorSearchView, VerificationTool},
  emits: ['committed','showLocation','openStreetView'],
  props: {
    listing: Listing,
    selectedLicense: License
  },
  data() {
    return {
      assessorProperties: [],
      nearbyRadius: 200,
      nearbyLimit: 10,
      nearbyProperties: [],
      isLoaded: false,
      loadingNearbyProperties: false,
      licenseQuery: '',
      isSearchingLicenses: false,
      licenses: null,
      tab: 'recommended'
    };
  },
  watch: {
    listing: {
      immediate: true,
      handler(newListing, oldListing) {
        if (newListing?.id !== oldListing?.id) {
          this.nearbyProperties = [];
          this.isLoaded = false;
          if (this.isAutoLoadRecommendedPropertiesEnabled) {
            this.refreshNearbyProperties();
          }
          if (this.$refs.scroller) {
            this.$refs.scroller.scrollTo(0, 0);
          }
        }
      }
    },
    tabs() {
      if (this.tabs.filter(tab => tab.id === this.tab).length === 0) {
        this.tab = this.tabs[0].id;
      }
    }
  },
  methods: {
    safeHref,

    async searchLicenses() {
      const query = String(this.licenseQuery || '').trim();
      if (query) {
        this.isSearchingLicenses = true;
        try {
          this.licenses = await License.searchByLicenseIdPrefix(this.city, query);
        } finally {
          this.isSearchingLicenses = false;
        }
      } else {
        this.licenses = null;
      }
    },
    async refreshNearbyProperties() {
      this.loadingNearbyProperties = true;
      try {
        const latlng = this.listing?.latlng;
        if (!Number.isFinite(latlng?.lat) || !Number.isFinite(latlng?.lng)) {
          this.nearbyProperties = [];
          return;
        }
        this.nearbyProperties = await PropertyDetails.findNearby(this.city, latlng, this.nearbyRadius, this.nearbyLimit);
      } finally {
        this.isLoaded = true;
        this.loadingNearbyProperties = false;
      }
    },
    async onAssessorProperties(properties) {
      this.assessorProperties = await PropertyDetails.resolveProperties(this.listing.city, properties);
    }
  },
  computed: {
    tabs() {
      let tabs = [
        {
          id: 'recommended',
          label: 'Recommendations',
          icon: 'magic'
        },
        {
          id: 'licenses',
          label: 'Existing Licenses',
          icon: 'file-earmark-check'
        },
        {
          id: 'assessor',
          label: 'Assessor Database',
          icon: 'search'
        }
      ];
      return tabs;
    },
    city() {
      return this.listing.city;
    },
    licenseProperties() {
      let properties = [];
      if (this.selectedLicense) {
        properties.push(this.selectedLicense.property);
      }
      this.licenses?.forEach(license => {
        properties.push(license.property);
      });
      return properties.filter(property => !!property);
    },
    isAutoLoadRecommendedPropertiesEnabled: {
      get() {
        const value = LocalStorage.get(AUTO_LOAD_RECOMMENDED_PROPERTIES_KEY);
        return value instanceof Boolean ? value : true;
      },
      set(value = false) {
        LocalStorage.set(AUTO_LOAD_RECOMMENDED_PROPERTIES_KEY, value);
      }
    }
  }
}
</script>

<style scoped>
/* Tab bar */
.tab-bar {
  background: #fff;
}

.tab-bar :deep(.btn) {
  border-radius: 0;
}
</style>
